// ═══════════════════════════════════════════════════════════
// Public Records Library — /portal/library
//
// Publicly accessible page showing all documents that staff
// have marked as "published" (isReleased + [PUBLISHED] in notes).
// No authentication required.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import {
  IconSearch, IconDownload, IconFileText, IconLoader2,
  IconFilter, IconSortDescending, IconPhoto, IconFileSpreadsheet,
  IconFile, IconCalendar,
} from '@tabler/icons-react';
import { listPublishedLibraryDocuments, getLibraryDownloadUrl, type LibraryDocument } from '../../api/library';
import { formatDate } from '../../lib/format';
import { useJurisdiction } from '../../contexts/JurisdictionContext';

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function mimeLabel(mimeType: string) {
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType.includes('word')) return 'Word';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Spreadsheet';
  if (mimeType.includes('csv')) return 'CSV';
  return 'File';
}

function mimeIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return IconPhoto;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) return IconFileSpreadsheet;
  if (mimeType === 'application/pdf' || mimeType.includes('word')) return IconFileText;
  return IconFile;
}

const TYPE_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'pdf', label: 'PDF' },
  { value: 'image', label: 'Images' },
  { value: 'spreadsheet', label: 'Spreadsheets' },
  { value: 'word', label: 'Word Docs' },
  { value: 'other', label: 'Other' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name', label: 'File Name A-Z' },
  { value: 'size', label: 'Largest First' },
];

function matchesType(doc: LibraryDocument, filter: string): boolean {
  if (filter === 'all') return true;
  const m = doc.mimeType || '';
  if (filter === 'pdf') return m === 'application/pdf';
  if (filter === 'image') return m.startsWith('image/');
  if (filter === 'spreadsheet') return m.includes('excel') || m.includes('spreadsheet') || m.includes('csv');
  if (filter === 'word') return m.includes('word');
  if (filter === 'other') return !m.startsWith('image/') && m !== 'application/pdf' && !m.includes('word') && !m.includes('excel') && !m.includes('spreadsheet') && !m.includes('csv');
  return true;
}

export default function Library() {
  const jurisdiction = useJurisdiction();
  const [docs, setDocs] = useState<LibraryDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  useEffect(() => {
    listPublishedLibraryDocuments({ anonymous: true })
      .then(setDocs)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = docs;

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.title.toLowerCase().includes(q) ||
        d.fileName.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q)
      );
    }

    // Type filter
    list = list.filter(d => matchesType(d, typeFilter));

    // Sort
    list = [...list].sort((a, b) => {
      if (sortBy === 'newest') return (b.uploadedAt ?? '').localeCompare(a.uploadedAt ?? '');
      if (sortBy === 'oldest') return (a.uploadedAt ?? '').localeCompare(b.uploadedAt ?? '');
      if (sortBy === 'name') return a.fileName.localeCompare(b.fileName);
      if (sortBy === 'size') return (b.fileSize || 0) - (a.fileSize || 0);
      return 0;
    });

    return list;
  }, [docs, search, typeFilter, sortBy]);

  // Group by month for display
  const grouped = useMemo(() => {
    const groups: { month: string; docs: LibraryDocument[] }[] = [];
    const map = new Map<string, LibraryDocument[]>();
    for (const doc of filtered) {
      const d = doc.uploadedAt ? new Date(doc.uploadedAt) : new Date();
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!map.has(key)) { map.set(key, []); groups.push({ month: label, docs: map.get(key)! }); }
      map.get(key)!.push(doc);
    }
    return groups;
  }, [filtered]);

  // Stats
  const categoryCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const d of docs) {
      const cat = d.category || 'Uncategorized';
      c[cat] = (c[cat] || 0) + 1;
    }
    return c;
  }, [docs]);

  async function handleDownload(doc: LibraryDocument) {
    setDownloadingId(doc.recordId);
    try {
      const url = await getLibraryDownloadUrl(doc, { anonymous: true });
      if (!url) return;
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
      {/* Header */}
      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 4px' }}>
        Public Records Library
      </h1>
      <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: '0 0 24px', lineHeight: 1.6 }}>
        {jurisdiction?.name ?? 'the jurisdiction'} — Previously released public records available for download.
        These records have been reviewed and released in response to prior public records requests.
      </p>

      {/* Stats strip */}
      {!loading && docs.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px',
            borderRadius: 4, fontSize: 12, fontWeight: 600,
            background: 'var(--color-primary-50)', color: 'var(--color-primary-700)',
          }}>
            {docs.length} documents
          </span>
          {Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
            <span key={cat} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px',
              borderRadius: 4, fontSize: 12, fontWeight: 500,
              background: 'var(--color-neutral-100)', color: 'var(--color-neutral-600)',
            }}>
              {cat} ({count})
            </span>
          ))}
        </div>
      )}

      {/* Search + Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <IconSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-400)' }} />
          <input
            type="text"
            className="form-input"
            style={{ paddingLeft: 32, fontSize: 14, width: '100%', boxSizing: 'border-box' }}
            placeholder="Search records…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ position: 'relative' }}>
          <IconFilter size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-400)', pointerEvents: 'none' }} />
          <select
            className="form-select"
            style={{ paddingLeft: 24, fontSize: 14 }}
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
          >
            {TYPE_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div style={{ position: 'relative' }}>
          <IconSortDescending size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-400)', pointerEvents: 'none' }} />
          <select
            className="form-select"
            style={{ paddingLeft: 24, fontSize: 14 }}
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--color-neutral-400)' }}>
          <IconLoader2 size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
          <div style={{ fontSize: 14 }}>Loading records…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 24px',
          background: 'white', borderRadius: 4,
          border: '1px solid var(--color-neutral-200)',
          color: 'var(--color-neutral-400)',
        }}>
          <IconFileText size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-600)', marginBottom: 4 }}>
            {search || typeFilter !== 'all' ? 'No records match your filters' : 'No records have been published yet'}
          </div>
          <div style={{ fontSize: 14 }}>
            {search || typeFilter !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Records will appear here once staff review and publish them.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {grouped.map(group => (
            <div key={group.month}>
              {/* Month header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <IconCalendar size={14} style={{ color: 'var(--color-neutral-400)' }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {group.month}
                </span>
                <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>({group.docs.length})</span>
              </div>

              {/* Document cards */}
              <div style={{ background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 4, overflow: 'hidden' }}>
                {group.docs.map((doc, i) => {
                  const FileIcon = mimeIcon(doc.mimeType);
                  return (
                    <div
                      key={doc.recordId}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 16,
                        padding: '12px 16px',
                        borderBottom: i < group.docs.length - 1 ? '1px solid var(--color-neutral-100)' : 'none',
                        transition: 'background 100ms',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-neutral-50)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      onClick={() => handleDownload(doc)}
                    >
                      {/* File icon */}
                      <div style={{
                        width: 40, height: 40, borderRadius: 4,
                        background: 'var(--color-primary-50)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <FileIcon size={20} style={{ color: 'var(--color-primary-700)' }} />
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {doc.title || doc.fileName}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px', marginTop: 2 }}>
                          {doc.category && (
                            <span style={{
                              display: 'inline-block', padding: '1px 4px', borderRadius: 4,
                              fontSize: 12, fontWeight: 600, background: 'var(--color-primary-50)', color: 'var(--color-primary-700)',
                            }}>
                              {doc.category}
                            </span>
                          )}
                          <span style={{
                            display: 'inline-block', padding: '1px 4px', borderRadius: 4,
                            fontSize: 12, fontWeight: 600, background: 'var(--color-neutral-100)', color: 'var(--color-neutral-600)',
                          }}>
                            {mimeLabel(doc.mimeType)}
                          </span>
                          {doc.fileSize > 0 && <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>{formatBytes(doc.fileSize)}</span>}
                          {doc.uploadedAt && <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>Uploaded {formatDate(doc.uploadedAt)}</span>}
                        </div>
                        {doc.description && (
                          <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', marginTop: 4, lineHeight: 1.5 }}>
                            {doc.description}
                          </div>
                        )}
                      </div>

                      {/* Download button */}
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                        onClick={e => { e.stopPropagation(); handleDownload(doc); }}
                        disabled={downloadingId === doc.recordId}
                      >
                        {downloadingId === doc.recordId
                          ? <IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          : <IconDownload size={14} />
                        }
                        Download
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
